import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CheckIcon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';

const normalizeIban = (value = '') => value.toString().replace(/[^0-9a-z]/gi, '').toUpperCase();

const formatIban = (value = '', maxLen) => {
  const normalized = normalizeIban(value);
  const clipped = typeof maxLen === 'number' ? normalized.slice(0, maxLen) : normalized;
  return clipped.replace(/(.{4})/g, '$1 ').trim();
};

const isValidFrenchIban = (value = '') => {
  const iban = normalizeIban(value);
  return iban.length === 27 && /^FR[0-9A-Z]{25}$/.test(iban);
};

const UserEdit = () => {
  // ===== HOOKS ET CONTEXTE =====
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user: currentUser, refreshUserData } = useContext(AuthContext);
  
  // Vérifier si l'utilisateur est en mode consultation (lecture seule)
  const isReadOnly = currentUser && currentUser.role === 'consultation';
  
  // ===== ÉTATS =====
  // États du formulaire principal
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    birthDate: '',
    birthPlace: '',
    socialSecurityNumber: '',
    showLeaveNumber: '',
    phone: '',
    iban: '',
    isPermanent: false,
    role: ''
  });
  
  // États du formulaire de mot de passe
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  
  // États des messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // États d'affichage
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  
  // États pour la confirmation par mot de passe
  const [adminPassword, setAdminPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // États pour la validation
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    phone: '',
    socialSecurityNumber: ''
  });

  // ===== EFFETS =====
  // Redirection si l'utilisateur n'est pas administrateur (sauf consultation qui peut voir en lecture seule)
  useEffect(() => {
    if (currentUser && currentUser.role !== 'administrateur' && currentUser.role !== 'consultation') {
      navigate('/users');
      return;
    }
  }, [currentUser, navigate]);
  
  // Chargement des données de l'utilisateur
  useEffect(() => {
    // Charger les données si l'utilisateur est administrateur ou en consultation
    if (currentUser && (currentUser.role === 'administrateur' || currentUser.role === 'consultation')) {
      const fetchUser = async () => {
        try {
          const response = await api.get(`/users/${id}`);
          const userData = response.data.data.user;
          setFormData({
            ...userData,
            birthDate: userData.birthDate && userData.birthDate !== 'Invalid Date' && !isNaN(new Date(userData.birthDate)) ? new Date(userData.birthDate).toISOString().split('T')[0] : '',
            iban: formatIban(userData.iban || '', 27)
          });
        } catch (err) {
          setError(err.response?.data?.message || 'Erreur lors du chargement des données');
        }
      };

      fetchUser();
    }
  }, [id, api, currentUser]);

  // ===== GESTIONNAIRES D'ÉVÉNEMENTS =====
  // Gestion des changements dans le formulaire principal
  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'iban' ? formatIban(value, 27) : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
    
    // Validation en temps réel
    validateField(name, nextValue);
  };
  
  // Gestion des changements dans le formulaire de mot de passe
  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };
  
  // Soumission du formulaire principal
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Si l'utilisateur est en mode consultation, ne pas permettre la soumission
    if (isReadOnly) {
      return;
    }
    
    // Validation des champs
    let isValid = true;
    const errors = {
      email: '',
      phone: '',
      socialSecurityNumber: ''
    };
    
    // Validation de l'email
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
      isValid = false;
    }
    
    // Validation du téléphone
    if (formData.phone && formData.phone.length !== 10) {
      errors.phone = 'Le numéro de téléphone doit contenir exactement 10 chiffres';
      isValid = false;
    }
    
    // Validation du numéro de sécurité sociale
    if (formData.socialSecurityNumber) {
      const cleanedValue = formData.socialSecurityNumber.replace(/\s/g, '');
      if (cleanedValue.length !== 15) {
        errors.socialSecurityNumber = 'Le numéro de sécurité sociale doit contenir exactement 15 chiffres';
        isValid = false;
      }
    }
    
    setValidationErrors(errors);
    
    if (!isValid) {
      setError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    // Afficher la modal de confirmation
    setShowConfirmModal(true);
  };
  
  // Soumission après confirmation du mot de passe
  const handleConfirmedSubmit = async () => {
    try {
      // Vérifier le mot de passe avant de procéder
      if (!adminPassword) {
        setPasswordError('Veuillez entrer votre mot de passe');
        return;
      }
      
      // Vérifier le mot de passe auprès du serveur
      try {
        const verifyResponse = await api.post('/users/verify-password', {
          password: adminPassword
        });
        
        if (!verifyResponse.data.success) {
          setPasswordError('Mot de passe incorrect');
          return;
        }
      } catch (verifyErr) {
        setPasswordError(verifyErr.response?.data?.message || 'Erreur de vérification du mot de passe');
        return;
      }
      
      // Créer l'objet de données à envoyer
      const dataToSend = { 
        ...formData
      };

      const normalizedIban = normalizeIban(dataToSend.iban);
      if (normalizedIban && !isValidFrenchIban(normalizedIban)) {
        setError('IBAN invalide. Exemple : FR76 1234 5123 4512 3456 7890 123');
        setShowConfirmModal(false);
        return;
      }
      dataToSend.iban = normalizedIban || null;
      
      // Ajouter le mot de passe s'il est fourni
      if (passwordData.password) {
        if (passwordData.password !== passwordData.confirmPassword) {
          setError('Les mots de passe ne correspondent pas');
          setShowConfirmModal(false);
          return;
        }
        
        if (passwordData.password.length < 8) {
          setError('Le mot de passe doit contenir au moins 8 caractères');
          setShowConfirmModal(false);
          return;
        }
        
        dataToSend.password = passwordData.password;
      }
      
      // Envoyer les données au serveur
      await api.put(`/users/${id}`, dataToSend);
      setSuccess('Profil mis à jour avec succès');
      setShowConfirmModal(false);
      setAdminPassword('');
      
      // Si l'utilisateur modifié est l'utilisateur actuellement connecté, rafraîchir ses données
      if (currentUser && currentUser.id.toString() === id.toString()) {
        await refreshUserData();
      }
      
      // Rediriger vers la liste des utilisateurs après un court délai
      setTimeout(() => navigate('/users'), 2000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      if (err.response?.status === 401) {
        setPasswordError('Mot de passe incorrect');
      } else {
        setError(err.response?.data?.message || 'Erreur lors de la mise à jour de l\'utilisateur');
        setShowConfirmModal(false);
      }
    }
  };

  // ===== FONCTIONS UTILITAIRES =====
  // Validation des champs
  const validateField = (name, value) => {
    let newErrors = { ...validationErrors };
    
    switch (name) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        newErrors.email = emailRegex.test(value) ? '' : 'Format d\'email invalide';
        break;
      case 'phone':
        newErrors.phone = value.length === 10 ? '' : 'Le numéro de téléphone doit contenir 10 chiffres';
        break;
      case 'socialSecurityNumber':
        const digitsOnly = value.replace(/\D/g, '');
        newErrors.socialSecurityNumber = digitsOnly.length === 15 ? '' : 'Le numéro de sécurité sociale doit contenir 15 chiffres';
        break;
      default:
        break;
    }
    
    setValidationErrors(newErrors);
  };

  // ===== RENDU =====
  return (
    <div className="max-w-4xl mx-auto relative">
      {/* Image de théâtre floutée en arrière-plan de toute la page */}
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-25 z-0" 
        style={{ 
          backgroundImage: 'url("/images/theatre-background.jpg")', 
          filter: 'blur(2px)',
          top: '64px' // Commence après la navbar
        }}
      ></div>
      
      {/* En-tête */}
      <div className="bg-white/90 rounded-xl shadow-lg overflow-hidden mb-8 border-t-4 border-t-gold relative z-10">
        <div className="bg-gradient-to-r from-primary/90 to-accent/90 text-white p-6">
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-white border-4 border-white shadow-lg mr-6 flex items-center justify-center">
              <UserCircleIcon className="h-12 w-12 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Modifier l'utilisateur</h1>
              <p className="text-white/80 mt-1">{formData.firstName} {formData.lastName}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages de succès et d'erreur */}
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded shadow-md relative z-10">
          <p>{success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-md relative z-10">
          <div className="flex">
            <div className="py-1">
              <XMarkIcon className="h-6 w-6 text-red-500 mr-3" />
            </div>
            <div>
              <p className="font-bold">Erreur</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire */}
      <div className="bg-white/90 rounded-xl shadow-lg overflow-hidden relative z-10">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Informations personnelles */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-primary border-b-2 border-gold pb-2 mb-4">
                  Informations personnelles
                </h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    className={`form-input w-full rounded-md border-gray-300 shadow-sm ${isReadOnly ? 'bg-gray-100' : 'focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50'} ${validationErrors.email ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 ${validationErrors.phone ? 'border-red-500' : ''}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Exactement 10 chiffres requis</p>
                  {validationErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse (code postal + ville)</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
              </div>
              
              {/* Informations professionnelles */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-primary border-b-2 border-gold pb-2 mb-4">
                  Informations professionnelles
                </h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lieu de naissance</label>
                  <input
                    type="text"
                    name="birthPlace"
                    value={formData.birthPlace}
                    onChange={handleChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de sécurité sociale</label>
                  <input
                    type="text"
                    name="socialSecurityNumber"
                    value={formData.socialSecurityNumber}
                    onChange={handleChange}
                    maxLength={30}
                    className={`form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 ${validationErrors.socialSecurityNumber ? 'border-red-500' : ''}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">15 chiffres requis</p>
                  {validationErrors.socialSecurityNumber && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.socialSecurityNumber}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de congé spectacle</label>
                  <input
                    type="text"
                    name="showLeaveNumber"
                    value={formData.showLeaveNumber}
                    onChange={handleChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                  <input
                    type="text"
                    name="iban"
                    value={formData.iban}
                    onChange={handleChange}
                    maxLength={33}
                    placeholder="FR76 1234 5123 4512 3456 7890 123"
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-gold focus:border-gold"
                  >
                    <option value="artistesProd">artistes en production</option>
                    <option value="artistesCoProd">artistes en Coproduction</option>
                    <option value="permanence">Permanence</option>
                    <option value="billeterie">Billetterie</option>
                    <option value="regie">Régie</option>
                    <option value="professeur">Professeur</option>
                    <option value="consultation">Consultation</option>
                    <option value="administrateur">Administrateur</option>
                  </select>
                </div>
                
                <div className="relative flex items-center p-2 group">
                  <div className="relative">
                    <input
                      id="isPermanent"
                      name="isPermanent"
                      type="checkbox"
                      checked={formData.isPermanent}
                      onChange={(e) => setFormData({...formData, isPermanent: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div onClick={() => setFormData({...formData, isPermanent: !formData.isPermanent})} className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary cursor-pointer"></div>
                  </div>
                  <div className="ml-3 block text-sm font-medium text-gray-700 group-hover:text-primary transition-colors duration-200">
                    <span className="relative">
                      Utilisateur autorisé
                      {formData.isPermanent && (
                        <span className="absolute -top-1 -right-4 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Section mot de passe */}
            <div className="mt-8">
              <button
                type="button"
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="text-primary font-medium flex items-center"
              >
                {showPasswordSection ? '- Masquer' : '+ Afficher'} la section mot de passe
              </button>
              
              {showPasswordSection && (
                <div className="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                  <h2 className="text-xl font-bold text-primary border-b-2 border-gold pb-2 mb-4">
                    Modifier le mot de passe
                  </h2>
                  

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                      <input
                        type="password"
                        name="password"
                        value={passwordData.password}
                        onChange={handlePasswordChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-8 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/users')}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors duration-300"
              >
                {isReadOnly ? 'Retour' : 'Annuler'}
              </button>
              {!isReadOnly && (
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-accent transition-colors duration-300"
                >
                  <CheckIcon className="h-5 w-5 mr-2" />
                  Enregistrer
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      
      {/* Modal de confirmation avec mot de passe */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmer les modifications</h3>
            <p className="text-gray-500 mb-4">
              Veuillez entrer votre mot de passe pour confirmer les modifications.
            </p>
            
            {/* Champ de mot de passe */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Votre mot de passe
              </label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Entrez votre mot de passe"
              />
              {passwordError && (
                <p className="mt-1 text-sm text-red-600">{passwordError}</p>
              )}
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setAdminPassword('');
                  setPasswordError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmedSubmit}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserEdit;
